import { useEffect } from "react";
import { Extension } from "@tiptap/core";
import type { Editor } from "@tiptap/react";
import { Plugin, type Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

/**
 * Marks the paragraph (or heading) holding the caret, so focus mode can dim the rest
 * (styles.css; only while <html data-focus="on">). Selecting a range marks nothing, and
 * the page's "focus-caret" class comes off, so all of a selection stays readable.
 * One decoration, so this is cheap even when focus mode is off.
 */
export const FocusBlock = Extension.create({
  name: "focusBlock",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          attributes: (state): Record<string, string> => (state.selection.empty ? { class: "focus-caret" } : {}),
          decorations(state) {
            const { $head, empty } = state.selection;
            if (!empty || !$head.parent.isTextblock) return null;
            const pos = $head.before();
            return DecorationSet.create(state.doc, [Decoration.node(pos, pos + $head.parent.nodeSize, { class: "focus-current" })]);
          },
        },
      }),
    ];
  },
});

/** Where the caret's line is held: this far down the visible area above the status line. */
const LINE_AT = 0.4;

/**
 * Typewriter scrolling: while typing or moving the caret with the keyboard, scrolls so the
 * caret's line sits at LINE_AT. Clicks and taps don't scroll (the text would slide out from
 * under the pointer); the next keystroke brings the line back. Edits arriving from another
 * device keep the line where it is. Uses the visual viewport, so it holds with the iOS keyboard up.
 */
export function useTypewriter(editor: Editor | null, on: boolean) {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!editor || !on) return;
    let frame = 0;
    const hold = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!editor.isFocused || editor.isDestroyed) return;
        const caret = editor.view.coordsAtPos(editor.state.selection.head);
        const statusLine = document.querySelector(".status-line")?.getBoundingClientRect().height ?? 0;
        const top = vv?.offsetTop ?? 0;
        const height = (vv?.height ?? window.innerHeight) - statusLine;
        const delta = (caret.top + caret.bottom) / 2 - (top + height * LINE_AT);
        // Instant: a smooth scroll is cut short by the next keystroke (or a held arrow key).
        if (Math.abs(delta) >= 1) window.scrollBy({ top: delta, behavior: "instant" });
      });
    };
    const onTransaction = ({ transaction: tr }: { transaction: Transaction }) => {
      if ((tr.docChanged || tr.selectionSet) && !tr.getMeta("pointer")) hold();
    };
    hold();
    editor.on("transaction", onTransaction);
    vv?.addEventListener("resize", hold);
    return () => {
      cancelAnimationFrame(frame);
      editor.off("transaction", onTransaction);
      vv?.removeEventListener("resize", hold);
    };
  }, [editor, on]);
}
